import React, { ReactNode } from 'react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorId?: string;
  errorType?: string;
  errorMessage?: string;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorId: '', errorType: '', errorMessage: '' };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Capturar la excepción en Sentry
    const sentryEventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });

    // Obtener el tipo de error y el mensaje
    const errorType = error.name || 'Unknown Error Type';
    const errorMessage = error.message || 'No error message provided';

    // Guardar el ID de error de Sentry, el tipo y el mensaje en el estado
    this.setState({
      errorId: sentryEventId,
      errorType,
      errorMessage,
    });
  }

  handleReload = () => {
    this.setState({ hasError: false, errorId: '', errorType: '', errorMessage: '' });
  };

  handlePageReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h1>Oops! Algo salió mal.</h1>
          <p>
            <strong>Tipo de error:</strong> {this.state.errorType}
          </p>
          <p>
            <strong>Mensaje del error:</strong> {this.state.errorMessage}
          </p>
          <p>
            Identificador del error: <strong>{this.state.errorId}</strong>
          </p>
          <button onClick={this.handleReload}>Intentar de nuevo</button>
          <button style={{ marginLeft: '10px' }} onClick={this.handlePageReload}>
            Recargar la página
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
